import { Colors, StepBadge, Text } from '@amelio/ui-library';

import type { ReactNode } from 'react';

import { QUESTION_STACK_GAP } from '@/pages/ThematicMeetings/components/meetingStyles';

/** Shared by the question and the box its badge is centred in, so the badge sits on the first line. */
const QUESTION_TEXT = 'text-[14px] leading-[22px]';

interface QuestionsWithNotesProps {
    questions: string[];
    /** The notes column: the field while the meeting is planned, what was written once completed. */
    children: ReactNode;
}

/**
 * The questions to think about, numbered, beside the notes they lead to, both aligned at the top;
 * stacked below the `lg` breakpoint, questions first.
 * An implicit grid is already a single column: no base `grid-cols-*` beside `lg:grid-cols-2` (see
 * src/responsive-utilities.css).
 */
export const QuestionsWithNotes = ({ questions, children }: QuestionsWithNotesProps) => (
    <div className={'grid items-start gap-10 lg:grid-cols-2'}>
        <ol className={`flex flex-col ${QUESTION_STACK_GAP}`}>
            {questions.map((question, index) => (
                <li
                    key={index}
                    className={'flex items-start gap-3'}
                >
                    {/* The list already numbers its items for assistive technologies: the badge
                        is the visible number only. One line tall whatever the badge's size, the
                        box keeps the badge on the first line of a question that wraps. */}
                    <div
                        aria-hidden={'true'}
                        className={`flex h-lh items-center ${QUESTION_TEXT}`}
                    >
                        <StepBadge
                            step={index + 1}
                            appearance={'ring'}
                            size={'xs'}
                        />
                    </div>

                    <Text
                        weight={'medium'}
                        color={Colors.BASE_600}
                        className={QUESTION_TEXT}
                    >
                        {question}
                    </Text>
                </li>
            ))}
        </ol>

        <div className={'flex flex-col gap-2'}>{children}</div>
    </div>
);
